import type {
  BackgroundImageList,
  RandomBackgroundImage,
} from "@sukha/api-contract";
import { apiUrl } from "@/lib/api/client";

/**
 * Background images, typed from the API's own response schemas (TX-4969).
 *
 * Both functions used to end in `return response.json()`, which is `any`. The
 * route could have dropped a field, renamed `url`, or started returning a bare
 * array, and every caller would have kept compiling — the defect class the
 * contract suite catches at runtime and the type system never saw.
 *
 * The return types now come from `@sukha/api-contract`, which is the same module
 * `apps/api/src/routes/backgroundImages.ts` builds its responses from. One
 * declaration, both sides. A shape change is a compile error here rather than a
 * surprise in a component.
 *
 * The schemas are NOT parsed at runtime on this side. Parsing every response
 * would double the validation the route already does and turn a contract
 * mismatch into a client-side throw; the value here is the compile-time link,
 * and the runtime check belongs where the data is produced.
 */
export const getBackgroundImages = async (
  page: number = 1,
  limit: number = 10
): Promise<BackgroundImageList> => {
  const response = await fetch(
    apiUrl(`/api/v1/background-images?page=${page}&limit=${limit}`),
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to get background images");
  }
  return response.json() as Promise<BackgroundImageList>;
};

export const getRandomBackgroundImage =
  async (): Promise<RandomBackgroundImage> => {
    const response = await fetch(apiUrl("/api/v1/background-images/random"), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || "Failed to get random background image"
      );
    }
    return response.json() as Promise<RandomBackgroundImage>;
  };
